<template>
	<v-col cols="12" class="pa-0">
		<div class="d-flex align-center pa-3 ga-2">
			<span class="text-body-2 text-grey-darken-2">
				{{ pluralize("concept", concepts.length, true) }}
			</span>
			<QcHelpIcon
				size="small"
				location="bottom"
				:text="overviewHelp"
				:learn-more-href="qcManualConceptsHref"
			/>
			<v-spacer />
			<!-- Combine select-mode (DCTL-2024): pick 2+ concepts to roll up. -->
			<template v-if="combineMode">
				<span class="text-body-2 text-grey-darken-2">
					{{ selectedIds.length }} selected
				</span>
				<!-- The tint/ink pair only rides the enabled state: Vuetify resets
				.v-btn--disabled.v-btn--variant-flat to opacity 1, and the utility
				classes are !important, so a disabled button wearing them would look
				enabled. Dropping them lets Vuetify's own flat-disabled grey show. -->
				<v-btn
					size="small"
					variant="flat"
					:class="
						canCombineSelection
							? 'bg-valence-info-tint text-valence-info-ink'
							: null
					"
					prepend-icon="mdi-set-merge"
					:disabled="!canCombineSelection"
					@click="openCombineDialog"
				>
					Combine
				</v-btn>
				<v-btn size="small" variant="text" @click="toggleCombineMode">
					Cancel
				</v-btn>
			</template>
			<template v-else>
				<v-btn
					size="small"
					variant="text"
					color="primary"
					prepend-icon="mdi-set-merge"
					:disabled="concepts.length < 2"
					@click="toggleCombineMode"
				>
					Combine
				</v-btn>
				<v-btn
					size="small"
					variant="flat"
					class="bg-provenance-b-tint text-provenance-b-ink"
					prepend-icon="mdi-plus"
					@click="$emit('create')"
				>
					{{ createLabel }}
				</v-btn>
			</template>
		</div>

		<v-expansion-panels
			v-if="concepts.length > 0"
			v-model="openPanelId"
			variant="accordion"
		>
			<!-- Each row is virtualized with LazyRender: an off-screen panel
			     collapses to a fixed-height placeholder, so a several-hundred-concept
			     list mounts only its visible window instead of every panel at once
			     (the tab-switch freeze). force-render pins the open panel so it
			     survives being scrolled out of view. -->
			<lazy-render
				v-for="concept in concepts"
				:key="concept.id"
				class="concept-lazy-row"
				:placeholder-height="72"
				:force-render="concept.id === openPanelId"
			>
				<v-expansion-panel :value="concept.id">
					<v-expansion-panel-title>
						<v-checkbox
							v-if="combineMode"
							:model-value="selectedIds.includes(concept.id)"
							density="compact"
							hide-details
							class="flex-grow-0 mr-2 my-n3"
							:aria-label="'Select ' + concept.preferredName + ' to combine'"
							@click.stop
							@update:model-value="toggleSelected(concept.id)"
						/>
						<!-- Name is the left-aligned scan column so every concept name
						     lines up; only the DACTYL id rides in the row (muted), and the
						     NCI source chips live in the detail below so many rolled-up
						     sources never push the name around. -->
						<span class="font-weight-bold flex-grow-1">
							{{ concept.preferredName }}
						</span>
						<v-chip
							size="small"
							color="amber-darken-2"
							variant="tonal"
							class="mr-2 my-n1 flex-shrink-0"
						>
							<v-icon start size="20">mdi-qrcode</v-icon>
							{{ concept.dactylConceptId }}
						</v-chip>
						<v-btn
							size="small"
							variant="text"
							color="grey-darken-1"
							icon="mdi-pencil"
							class="mr-1"
							:aria-label="'Edit ' + concept.preferredName"
							@click.stop="$emit('edit', concept)"
						/>
						<v-btn
							size="small"
							variant="text"
							color="grey-darken-1"
							icon="mdi-delete"
							class="mr-2"
							:aria-label="'Delete ' + concept.preferredName"
							@click.stop="confirmDelete(concept)"
						/>
					</v-expansion-panel-title>

					<v-expansion-panel-text>
						<v-card flat>
							<!-- Source concepts this Dactyl concept rolls up (NCI CUIs, and
							     MED-RT NUIs for MoA concepts). Shown here, not in the row, so
							     many rolled-up sources never crowd the scannable name; each
							     links back to its EVS entry in the right terminology. The
							     per-chip label follows the code system so a MED-RT NUI is
							     never mislabeled as NCI. -->
							<div
								v-if="sourceChips(concept).length"
								class="mb-2 d-flex flex-wrap align-center ga-1"
							>
								<span class="text-body-2 font-weight-bold mr-1">
									Source concepts:
								</span>
								<v-chip
									v-for="chip in sourceChips(concept)"
									:key="chip.code"
									link
									size="small"
									color="grey-darken-1"
									variant="tonal"
									:aria-label="
										'View ' + chip.code + ' in ' + chip.label + ' EVS'
									"
									@click.stop="$emit('open-evs', chip.code, chip.terminology)"
								>
									<v-icon start size="20">mdi-qrcode</v-icon>
									{{ chip.label }} {{ chip.code }}
								</v-chip>
							</div>

							<div
								v-if="concept.tagScope?.tagNames?.length"
								class="mb-2 d-flex flex-wrap align-center ga-1"
							>
								<span class="text-body-2 font-weight-bold mr-1">
									Tag scope:
								</span>
								<v-chip
									v-for="tagName in concept.tagScope.tagNames"
									:key="tagName"
									size="x-small"
									variant="flat"
									class="bg-surface-tint text-fg-1"
								>
									{{ tagName }}
								</v-chip>
							</div>

							<div
								v-if="concept.tagScope?.tagGroupNames?.length"
								class="mb-2 d-flex flex-wrap align-center ga-1"
							>
								<span class="text-body-2 font-weight-bold mr-1">
									Tag group scope:
								</span>
								<v-chip
									v-for="groupName in concept.tagScope.tagGroupNames"
									:key="groupName"
									size="x-small"
									variant="flat"
									class="bg-provenance-b-tint text-provenance-b-ink"
								>
									{{ groupName }}
								</v-chip>
							</div>

							<!-- Per-axis characterization scope (DCTL-2025): read-only DACTYL
							     id chips, one row per constrained axis, only when present. -->
							<div
								v-for="entry in axisScopeEntries(concept)"
								:key="entry.axis"
								class="mb-2 d-flex flex-wrap align-center ga-1"
							>
								<span class="text-body-2 font-weight-bold mr-1">
									{{ entry.label }} scope:
								</span>
								<v-chip
									v-for="id in entry.ids"
									:key="id"
									size="x-small"
									variant="tonal"
									color="amber-darken-2"
								>
									<v-icon start size="14">mdi-qrcode</v-icon>
									{{ id }}
								</v-chip>
							</div>

							<p class="text-body-2 font-weight-bold">Synonyms:</p>
							<ul class="pl-4">
								<li
									v-for="(syn, i) in concept.synonyms"
									:key="i"
									class="mb-1 text-body-2"
								>
									<v-icon
										v-if="syn.isPreferred"
										size="x-small"
										color="amber-darken-2"
										class="mr-1"
									>
										mdi-star
									</v-icon>
									{{ syn.synonym }}
									<span v-if="syn.source" class="text-grey-darken-2">
										({{ syn.source }})
									</span>
								</li>
							</ul>
						</v-card>
					</v-expansion-panel-text>
				</v-expansion-panel>
			</lazy-render>
		</v-expansion-panels>

		<v-list v-else>
			<v-list-item>
				<template #subtitle>
					<p class="text-body-2">
						No concepts here yet. Add one from NCI EVS, or create a new one.
					</p>
				</template>
			</v-list-item>
		</v-list>

		<v-dialog v-model="deleteDialog" max-width="440px">
			<v-card>
				<v-card-title class="text-body-1">Delete concept?</v-card-title>
				<v-card-text class="text-body-2">
					This soft-deletes
					<strong>{{ conceptToDelete?.preferredName }}</strong>
					({{ conceptToDelete?.dactylConceptId }}) and its synonyms.
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn variant="text" @click="deleteDialog = false"> Cancel </v-btn>
					<v-btn
						variant="flat"
						class="bg-valence-negative-tint text-valence-negative-ink"
						@click="performDelete"
					>
						Delete
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="combineDialog" max-width="560px">
			<v-card>
				<v-card-title class="text-body-1">
					Combine {{ selectedConcepts.length }} concepts?
				</v-card-title>
				<v-card-text class="text-body-2">
					<p class="mb-3">
						Pick the concept to keep (the survivor). The others are absorbed
						into it: their NCI concepts and synonyms move over, tag scopes
						merge, and the absorbed IDs retire. You can split them back out
						later by NCI ID.
					</p>
					<v-radio-group v-model="survivorId" density="compact" hide-details>
						<v-radio v-for="c in selectedConcepts" :key="c.id" :value="c.id">
							<template #label>
								<span class="font-weight-medium">
									{{ c.preferredName }}
								</span>
								<span class="text-grey-darken-2 ml-1">
									({{ c.dactylConceptId }})
								</span>
							</template>
						</v-radio>
					</v-radio-group>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn variant="text" @click="combineDialog = false"> Cancel </v-btn>
					<!-- Enabled-only tint/ink, same reason as the toolbar Combine. -->
					<v-btn
						variant="flat"
						:class="
							survivorId ? 'bg-valence-info-tint text-valence-info-ink' : null
						"
						:loading="combining"
						:disabled="!survivorId"
						@click="performCombine"
					>
						Combine
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-col>
</template>

<script>
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import QcHelpIcon from "@/pages/QualityControl/QcHelpIcon.vue";
import LazyRender from "@/pages/QualityControl/LazyRender.vue";
import {
	CONCEPTS_OVERVIEW_HELP,
	CHARACTERIZATION_OVERVIEW_HELP,
	QC_MANUAL_CONCEPTS_HREF,
} from "./conceptHelpStrings";
import {
	axisLabel,
	CHARACTERIZATION_AXES,
} from "@/constants/characterizationAxes";
import pluralize from "@/utils/pluralize";

export default {
	name: "DactylConceptList",
	components: { QcHelpIcon, LazyRender },

	props: {
		concepts: { type: Array, default: () => [] },
		// The concept type this list shows (drives the "New ..." label + empty text).
		conceptType: { type: String, default: "normalization" },
	},

	emits: ["edit", "create", "open-evs", "changed"],

	data() {
		return {
			qcManualConceptsHref: QC_MANUAL_CONCEPTS_HREF,
			// The open accordion panel's id (concept.id), or undefined when none is
			// open. Tracked so LazyRender can pin the open panel (force-render) and
			// not tear it down when it scrolls out of view.
			openPanelId: null,
			deleteDialog: false,
			conceptToDelete: null,
			// Combine select-mode (DCTL-2024).
			combineMode: false,
			selectedIds: [],
			combineDialog: false,
			survivorId: null,
			combining: false,
		};
	},

	computed: {
		// Overview help for this tab. The characterization axes explain that they
		// characterize papers (which scopes normalization) rather than rewriting
		// values on Normalize; the Normalization tab keeps the store overview.
		overviewHelp() {
			return CHARACTERIZATION_AXES.includes(this.conceptType)
				? CHARACTERIZATION_OVERVIEW_HELP
				: CONCEPTS_OVERVIEW_HELP;
		},
		// Rolling concepts up needs two or more of them. Named because both the
		// toolbar button's :disabled and its tint/ink classes read it.
		canCombineSelection() {
			return this.selectedIds.length >= 2;
		},
		// The selected concepts, in list order, for the survivor picker.
		selectedConcepts() {
			return this.concepts.filter((c) => this.selectedIds.includes(c.id));
		},
		// "New ..." button label, per the tab's concept type.
		createLabel() {
			const labels = {
				normalization: "New concept",
				disease: "New disease",
				anatomy: "New anatomy concept",
				drug_class: "New drug class",
				moa: "New mechanism of action",
			};
			return labels[this.conceptType] || "New Dactyl concept";
		},
	},

	methods: {
		pluralize,

		...mapActions(useMainStore, ["showNotification"]),

		// The source-code chips a concept rolls up, code-system-aware: each source link
		// carries its `codeSystem`, so a MED-RT NUI (MoA concepts) renders a "MED-RT"
		// chip that links back to the medrt terminology, while NCI CUIs render "NCI"
		// chips linking to their terminology - never mislabeling one as the other. Falls
		// back to the denormalized single CUI (always NCI) for a concept loaded without links.
		sourceChips(concept) {
			const links = Array.isArray(concept.nciLinks) ? concept.nciLinks : [];
			if (links.length) {
				return links.map((link) =>
					this.chipFromSource(
						link.nciConceptId,
						link.codeSystem,
						link.terminology,
					),
				);
			}
			return concept.nciConceptId
				? [this.chipFromSource(concept.nciConceptId, "NCI_EVS")]
				: [];
		},
		// Shape one source id into a display chip: label + link-back terminology. Prefer
		// the serialized `terminology` (which distinguishes ncit from ncim - both are the
		// NCI_EVS code system, so deriving from codeSystem alone would send an NCIt code
		// to the ncim endpoint); fall back to deriving from the code system.
		chipFromSource(code, codeSystem, terminology) {
			const isMedrt = codeSystem === "MED_RT";
			return {
				code,
				codeSystem: codeSystem || "NCI_EVS",
				label: isMedrt ? "MED-RT" : "NCI",
				terminology: terminology || (isMedrt ? "medrt" : "ncim"),
			};
		},

		// The concept's per-axis characterization scope (DCTL-2025), as display rows -
		// only axes with a non-empty id list. Keys iterated so a future axis needs no
		// change here; label falls back to the raw key for an unknown axis.
		axisScopeEntries(concept) {
			const axisScope = concept.tagScope?.axisScope;
			if (!axisScope || typeof axisScope !== "object") return [];
			return Object.keys(axisScope)
				.filter(
					(axis) => Array.isArray(axisScope[axis]) && axisScope[axis].length,
				)
				.map((axis) => ({
					axis,
					label: axisLabel(axis),
					ids: axisScope[axis],
				}));
		},

		toggleCombineMode() {
			this.combineMode = !this.combineMode;
			if (!this.combineMode) {
				this.selectedIds = [];
			}
		},
		toggleSelected(id) {
			const i = this.selectedIds.indexOf(id);
			if (i === -1) {
				this.selectedIds.push(id);
			} else {
				this.selectedIds.splice(i, 1);
			}
		},
		openCombineDialog() {
			// Default the survivor to the first selected concept.
			this.survivorId = this.selectedIds[0] ?? null;
			this.combineDialog = true;
		},
		performCombine() {
			if (!this.survivorId || this.selectedIds.length < 2) return;
			this.combining = true;
			this.$http
				.post("/concepts/combine", {
					ids: this.selectedIds,
					survivorId: this.survivorId,
				})
				.then(() => {
					this.showNotification({
						status: "success",
						message: "Successfully combined concepts",
					});
					this.combining = false;
					this.combineDialog = false;
					this.combineMode = false;
					this.selectedIds = [];
					this.survivorId = null;
					this.$emit("changed");
				})
				.catch((error) => {
					const message =
						error?.response?.data?.message ??
						"A problem occurred while combining the concepts";
					this.showNotification({ status: "error", message });
					this.combining = false;
				});
		},

		confirmDelete(concept) {
			this.conceptToDelete = concept;
			this.deleteDialog = true;
		},
		performDelete() {
			const concept = this.conceptToDelete;
			if (!concept) return;

			this.$http
				.post("/concepts/delete", { id: concept.id })
				.then(() => {
					this.showNotification({
						status: "success",
						message: "Successfully deleted concept",
					});
					this.deleteDialog = false;
					this.conceptToDelete = null;
					this.$emit("changed");
				})
				.catch((error) => {
					const message =
						error?.response?.data?.message ??
						"A problem occurred while deleting the concept";
					this.showNotification({ status: "error", message });
					this.deleteDialog = false;
				});
		},
	},
};
</script>

<style scoped>
/* Each concept row is wrapped in <lazy-render> for virtualization, so the
   wrapper - not the v-expansion-panel - is the direct flex child of
   v-expansion-panels. Vuetify's `> .v-expansion-panel { flex: 1 0 100% }` rule
   no longer matches, which left rows sizing to content and wrapping
   side-by-side; restore full-width vertical stacking on the wrapper. */
.concept-lazy-row {
	flex: 1 0 100%;
	max-width: 100%;
}

/* The wrapper also breaks Vuetify's accordion chrome: the between-panel divider
   and first/last-child corner rounding target the panel as a direct child, so
   each panel instead rounds all four of its own corners - and the curves leave
   small triangular gaps against the row edges. Square the panels (the container
   still clips the list's outer corners) and draw a single faint divider on the
   wrapper, so the line is consistent whether the row is a rendered panel or a
   placeholder. */
.concept-lazy-row:not(:last-child) {
	/* Faint row divider. A white low-alpha line reads as a soft hairline over the
	   card seam here, whereas a black low-alpha line looked too heavy on these
	   tall, sparse rows. (Light-theme UI, so the white composite is stable.) */
	border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.concept-lazy-row :deep(.v-expansion-panel),
.concept-lazy-row :deep(.v-expansion-panel::after) {
	border-radius: 0;
}

/* The placeholder just reserves the row height; the wrapper draws the divider. */
.concept-lazy-row :deep(.lazy-placeholder) {
	margin: 0;
	border: none;
	border-radius: 0;
	background: transparent;
}
</style>
